import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import useUpdateEffect from "../../hooks/useUpdateEffect";
import Loading from "../Loading";
import ReactDOM from "react-dom";

const DropdownContext = createContext();
const generateId = () => `dropdown-${Math.random().toString(36).substring(2, 6)}`;

export default function usePlatformDetection() {
  const [platform, setPlatform] = useState('unknown');

  useEffect(() => {
    const userPlatform = navigator.platform.toLowerCase();
    if (userPlatform.includes('mac')) {
      setPlatform('mac');
    } else if (userPlatform.includes('win')) {
      setPlatform('windows');
    } else {
      setPlatform('other');
    }
  }, []);

  return platform;
}

export const Dropdown = ({
  children,
  className = "",
  parent,
  triggers = [],
  defaultZIndex = 0,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const scrollYRef = useRef(0);

  const uniqueIdRef = useRef(generateId());
  const platform = usePlatformDetection();
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Check immediately on component mount
    checkScreenSize();
    
    // Also listen for resize events
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);
  // Handle page scrolling when dropdown is open
  useEffect(() => {
    try {
      if (isOpen) {
        // Save the current scroll position
        scrollYRef.current = window.scrollY;
        
        // Check if page has a scrollbar before applying scrollbar compensation
        const hasVerticalScrollbar = window.innerWidth > document.documentElement.clientWidth;
        
        // Prevent scrolling
        if (document.body) {
          document.body.style.overflow = 'hidden';
          
          // Only add padding if scrollbar exists
          if (hasVerticalScrollbar && platform === 'windows' && !isMobile) {
            document.body.style.paddingRight = '15px';
          }
        }
        
        // Try to find the header element using different selectors
        const headerElement = 
          document.querySelector('header') ||
          document.querySelector('.header') ||
          document.getElementById('header');
        
        // Only modify header if it exists and scrollbar exists
        if (headerElement && hasVerticalScrollbar && platform === 'windows' && !isMobile) {
          headerElement.style.paddingRight = '15px';
        }
      } else {
        // Restore scrolling and remove padding
        if (document.body) {
          document.body.style.overflow = 'auto';
          if (platform === 'windows' && !isMobile) {
            document.body.style.paddingRight = '0px';
          }
        }
        
        // Try to find the header element using different selectors
        const headerElement = 
          document.querySelector('header') ||
          document.querySelector('.header') ||
          document.getElementById('header');
        
        // Only modify header if it exists
        if (headerElement && platform === 'windows' && !isMobile) {
          headerElement.style.paddingRight = '0px';
        }
      }
    } catch (error) {
      console.error("Error handling scroll behavior:", error);
      // Fallback to basic scroll prevention
      if (document.body) {
        document.body.style.overflow = isOpen ? 'hidden' : 'auto';
      }
    }
  }, [isOpen, platform, isMobile]);


  useEffect(() => {
    if (parent) {
      if (isOpen) document.querySelector(parent).style.zIndex = 60;
      else document.querySelector(parent).style.zIndex = defaultZIndex || 0;
    }
  }, [isOpen]);

  useUpdateEffect(() => {
    if (triggers.some((trigger) => trigger === "loading")) setIsLoading(true);
    else {
      setIsOpen(false);
      setIsLoading(false);
    }
  }, triggers);

  return (
    <DropdownContext.Provider value={{ isOpen, setIsOpen, isLoading ,uniqueId: uniqueIdRef.current}}>
      <div
        onClick={() => setIsOpen(false)}
        className={`fixed inset-0 ${
          isOpen ? "pointer-events-auto z-20" : "pointer-events-none"
        }`}
      ></div>

      <div className={`dropdown-container relative ${isOpen ? "z-30" : "z-0"} ${className}`}>
        {children}
      </div>
    </DropdownContext.Provider>
  );
};

export const DropdownTrigger = ({ children, className = "", ...props }) => {
  const { setIsOpen,uniqueId } = useContext(DropdownContext);

  return (
    <button
      type="button"
      onClick={() => setIsOpen((prev) => !prev)}
      className={`dropdown-trigger w-full h-full flex justify-center items-center ${className} ${uniqueId}`}
      {...props}
    >
      {children}
    </button>
  );
};

export const DropdownMenu = ({ children, state, className = "", ...props }) => {
  const { isOpen,uniqueId } = useContext(DropdownContext);
  const [triggerRect, setTriggerRect] = useState(null);

  // Save the trigger element's position before rendering the portal
  useEffect(() => {
    if (isOpen) {
      const triggerEl = document.querySelector(`.${uniqueId}`);
      if (triggerEl) {
        setTriggerRect(triggerEl.getBoundingClientRect());
      }
    }
  }, [isOpen,uniqueId]);

  const menuContent = (
    <div
      className={`min-w-max fixed bg-white border transition-all duration-200 p-1 rounded-md z-[60] ${
        isOpen
          ? "opacity-100 pointer-events-auto"
          : "opacity-0 pointer-events-none"
      } ${className}`}
      style={{
        top: "var(--dropdown-top)",
        left: "var(--dropdown-left)",
        transform: "var(--dropdown-transform)",
        width: "var(--dropdown-width, auto)"
      }}
      ref={(node) => {
        if (node && isOpen && triggerRect) {
          const menuRect = node.getBoundingClientRect();

          // Calculate available space in different directions
          const spaceBelow = window.innerHeight - triggerRect.bottom;
          const spaceAbove = triggerRect.top;
          const spaceRight = window.innerWidth - triggerRect.left;
          const spaceLeft = triggerRect.left;

          // Determine if we should open upward or downward
          let topPosition;
          let transform = "";

          if (spaceBelow < menuRect.height && spaceAbove > menuRect.height) {
            // Open upward if not enough space below but enough space above
            topPosition = `${triggerRect.top}px`;
            transform += "translateY(-100%)";
          } else {
            // Open downward by default
            topPosition = `${triggerRect.bottom}px`;
          }

          // Determine if we should open left or right
          let leftPosition;

          if (spaceRight < menuRect.width && spaceLeft > menuRect.width) {
            // Open to the left if not enough space on the right but enough on the left
            leftPosition = `${triggerRect.right}px`;
            transform += transform ? " " : "";
            transform += "translateX(-100%)";
          } else {
            // Open to the right by default
            leftPosition = `${triggerRect.left}px`;
          }

          node.style.setProperty("--dropdown-top", topPosition);
          node.style.setProperty("--dropdown-left", leftPosition);
          node.style.setProperty("--dropdown-transform", transform);
          node.style.setProperty("--dropdown-width", `${triggerRect.width}px`);
        }
      }}
      {...props}
    >
      {state === "loading" && (
        <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2">
          <Loading size={20} />
        </div>
      )}

      <ul className={`${state === "loading" ? "opacity-0" : "opacity-100"} overflow-visible max-h-[300px] overflow-y-auto`}>
        {children}
      </ul>
    </div>
  );

  // Create portal container if it doesn't exist
  useEffect(() => {
    if (!document.getElementById('dropdown-portal-container')) {
      const portalContainer = document.createElement('div');
      portalContainer.id = 'dropdown-portal-container';
      document.body.appendChild(portalContainer);
    }
  }, []);

  return ReactDOM.createPortal(
    menuContent,
    document.getElementById('dropdown-portal-container') || document.body
  );
};

export const DropdownOption = ({
  children,
  type = "",
  className = "",
  isPreventClose,
  onClick = () => {},
  ...props
}) => {
  const { isLoading,setIsOpen } = useContext(DropdownContext);

  return (
    <li
      {...props}
      onClick={() => {
        if (!isLoading){
          onClick()
          if(isPreventClose !== true){
            setIsOpen((prev) => !prev)
          }
        }else{
          if(isPreventClose !== true){
            setIsOpen((prev) => !prev)
          }
        };
      }}
      className={`!leading-none text-[15px] py-2 px-2 rounded-[5px] group relative overflow-visible ${
        isLoading
          ? "cursor-default"
          : type !== "heading"
          ? "cursor-pointer hover:bg-[#F9F9FA]"
          : ""
      } ${className}`}
    >
      <span
        className={`${type === "danger" ? "text-cherry" : ""} ${
          type === "success" ? "text-green-600" : ""
        } ${type === "heading" ? "font-semibold" : ""} ${
          isLoading ? "opacity-60" : "opacity-100"
        }`}
      >
        {typeof children === "object" ? children[0] : children}
      </span>

      {typeof children === "object" ? children[1] : null}
    </li>
  );
};

export const SubDropdownMenu = ({
  children,
  className = "",
  state,
  index,
  ...props
}) => {
  return (
    <div
      {...props}
      className={`fixed opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all duration-200 bg-white border rounded-md p-1 text-black w-max z-50 ${className}`}
      ref={(node) => {
        if (node) {
          const parent = node.closest(index || 'li');
          if (parent) {
            const rect = parent.getBoundingClientRect();
            const menuRect = node.getBoundingClientRect();
            
            // Determine position based on available space
            const spaceRight = window.innerWidth - rect.right;
            const spaceBelow = window.innerHeight - rect.top;
            
            // Position relative to the viewport rather than the parent
            node.style.position = 'fixed';
            
            // Set default positions to be at the right side of the parent
            let leftPos = rect.right;
            let topPos = rect.top;
            
            // Adjust if not enough space on the right
            if (spaceRight < menuRect.width && rect.left > menuRect.width) {              
              leftPos = rect.left - menuRect.width; 
            }
            
            // // Adjust if not enough space below
            if (spaceBelow < menuRect.height) {
              // Position upward as needed but ensure it stays within viewport
              const adjustment = Math.min(menuRect.height - rect.height, rect.top);
              topPos = rect.top - adjustment;
            }
            
            node.style.left = `${leftPos}px`;
            node.style.top = `${topPos}px`;
          }
        }
      }}
    >
      {state === "loading" && (
        <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2">
          <Loading size={20} />
        </div>
      )}

      <ul className={`${state === "loading" ? "opacity-0" : "opacity-100"} max-h-[300px] overflow-y-auto`}>
        {children}
      </ul>
    </div>
  );
};
